import { getIn } from 'formik';
import React, {
  FC,
} from 'react';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';
import { ManifestIntentionFielsProps } from './types';
import Datepicker from 'components/base/Datepicker';
import Input from 'components/base/Input';

import Select from 'components/base/Select';
import SelectCountryProvinceCity from 'components/base/SelectCountryProvinceCity';
import Textarea from 'components/base/Textarea';
import useCemetery from 'hooks/useCemetery';
import useCeremonyLocation from 'hooks/useCeremonyLocation';
import useCrematory from 'hooks/useCrematory';
import { useVigilLocation } from 'hooks/useVigilLocation';
import { CemeteryType } from 'models/CemeteryType';
import { CrematoryType } from 'models/CrematoryType';
import {
  IMasterDataDto,
} from 'models/MasterData';
import { VigilLocation } from 'models/VigilLocation';
import { RootState } from 'store';

const ManifestIntentionFields: FC<ManifestIntentionFielsProps> = ({
  disabled,
  formikProps: {
    errors, handleBlur, isSubmitting, setFieldValue, values,
  },
}) => {
  const { t } = useTranslation();
  const { ceremonies } = useSelector((state: RootState) => state.masterData);
  const { normalized } = useSelector((state: RootState) => state);

  const { cemeteries, normalizedCemeteries, getCemetery } = useCemetery(
    values.intermentService?.intermentAddress?.city,
  );
  const { vigilLocations, normalizedVigilLocations } = useVigilLocation(
    values.vigilService?.address?.city,
  );
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { crematories, normalizedCrematories } = useCrematory(
    values.cremationService?.address?.city,
  );
  const {
    ceremonyLocations,
    normalizedCeremonyLocations,
  // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useCeremonyLocation(values.ceremonyService?.ceremonyAddress?.city);

  return (
    <React.Fragment>
      <fieldset>
        <legend>{t('record.manifestIntention')}</legend>
        <Input
          checked={!!values.intermentService}
          disabled={disabled || isSubmitting || values.hasPickUp}
          error={getIn(errors, 'intermentService')}
          id="intermentService"
          name="intermentService"
          placeholder={t('service.INHUMACION')}
          type="checkbox"
          onBlur={handleBlur}
          onChange={(val) => {
            setFieldValue('intermentService', val ? {} : null);
          }}
        />
        <div />
        <SelectCountryProvinceCity
          key="intermentAddress"
          cityProps={{
            error: getIn(errors, 'intermentService.intermentAddress.city'),
            getLabel: ({ description }) => description,
            getValue: ({ code }) => code,
            name: 'intermentAddress.c',
            onBlur: handleBlur,
            onChange: (val: string) => {
              setFieldValue('intermentService.intermentAddress.city', val);
              setFieldValue('intermentService.cemetery', undefined);
            },
            placeholder: t('common.city'),
            value: values.intermentService?.intermentAddress?.city || '',
          }}
          countryProps={{
            error: getIn(
              errors,
              'intermentService.intermentAddress.country',
            ),
            getLabel: ({ description }) => description,
            getValue: ({ code }) => code,
            name: 'intermentAddress.co',
            onBlur: handleBlur,
            onChange: (val: string) => setFieldValue(
              'intermentService.intermentAddress.country',
              val,
            ),
            placeholder: t('common.country'),
            value: values.intermentService?.intermentAddress?.country || '',
          }}
          disabled={disabled || isSubmitting || !values.intermentService}
          initialize={false}
          provinceProps={{
            error: getIn(
              errors,
              'intermentService.intermentAddress.province',
            ),
            getLabel: ({ description }) => description,
            getValue: ({ code }) => code,
            name: 'intermentAddress.p',
            onBlur: handleBlur,
            onChange: (val: string) => setFieldValue(
              'intermentService.intermentAddress.province',
              val,
            ),
            placeholder: t('common.province'),
            value: values.intermentService?.intermentAddress?.province || '',
          }}
        />
        <Select<CemeteryType>
          key="cemetery"
          disabled={
            isSubmitting
            || disabled
            || !values.intermentService?.intermentAddress?.city
          }
          error={getIn(errors, 'intermentService.cemetery')}
          getLabel={({ description }) => description}
          getValue={({ code }) => code}
          name="cemetery"
          options={cemeteries}
          placeholder={t('service.interment.cemetery')}
          testid="interment-cemetery"
          value={
            values.intermentService?.cemetery
              ? normalizedCemeteries[values.intermentService?.cemetery]
              : undefined
          }
          searchable
          onBlur={handleBlur}
          onChange={(val) => {
            setFieldValue('intermentService.cemetery', val?.code);
            const selectedCemetery = getCemetery(val?.code);
            if (selectedCemetery) {
              setFieldValue('intermentService.cemeteryCoffin', selectedCemetery?.coffinInterment);
              setFieldValue('intermentService.cemeteryAshes', selectedCemetery?.ashesInterment);
            }
          }}
        />
        <Datepicker
          key="intermentDate"
          disabled={disabled || isSubmitting || !values.intermentService}
          error={getIn(errors, 'intermentService.intermentDate')}
          name="intermentDate"
          placeholder={t('service.interment.intermentDate')}
          selected={values.intermentService?.intermentDate}
          onBlur={handleBlur}
          onChange={(val) => setFieldValue('intermentService.intermentDate', val)}
        />
        <Datepicker
          key="intermentTime"
          disabled={disabled || isSubmitting || !values.intermentService}
          error={getIn(errors, 'intermentService.intermentTime')}
          name="intermentTime"
          placeholder={t('service.interment.intermentTime')}
          selected={values.intermentService?.intermentTime}
          showTimeSelectOnly
          onBlur={handleBlur}
          onChange={(val) => setFieldValue('intermentService.intermentTime', val)}
        />
        <Textarea
          key="intermentComment"
          disabled={isSubmitting || disabled || !values.intermentService}
          error={getIn(errors, 'intermentService.intermentComment')}
          name="intermentComment"
          placeholder={t('common.observations')}
          value={values.intermentService?.intermentComment || ''}
          onBlur={handleBlur}
          onChange={(val) => setFieldValue('intermentService.intermentComment', val)}
        />
        <div />

        <Input
          checked={!!values.cremationService}
          disabled={disabled || isSubmitting || values.hasPickUp}
          error={getIn(errors, 'cremationService')}
          id="cremationService"
          name="cremationService"
          placeholder={t('service.INCINERACION')}
          type="checkbox"
          onBlur={handleBlur}
          onChange={(val) => {
            setFieldValue('cremationService', val ? {} : null);
          }}
        />
        <div />
        <SelectCountryProvinceCity
          key="address"
          cityProps={{
            error: getIn(errors, 'cremationService.address.city'),
            getLabel: ({ description }) => description,
            getValue: ({ code }) => code,
            name: 'address.c',
            onBlur: handleBlur,
            onChange: (val: string) => {
              setFieldValue('cremationService.address.city', val);
              setFieldValue(
                'cremationService.crematorium',
                undefined,
              );
              setFieldValue('cremationService.address.deposit', undefined);
              setFieldValue('cremationService.address.oven', undefined);
            },
            placeholder: t('common.city'),
            value: values.cremationService?.address?.city || '',
          }}
          countryProps={{
            error: getIn(errors, 'cremationService.address.country'),
            getLabel: ({ description }) => description,
            getValue: ({ code }) => code,
            name: 'address.co',
            onBlur: handleBlur,
            onChange: (val: string) => setFieldValue('cremationService.address.country', val),
            placeholder: t('common.country'),
            value: values.cremationService?.address?.country || '',
          }}
          disabled={disabled || isSubmitting || !values.cremationService}
          initialize={false}
          provinceProps={{
            error: getIn(errors, 'cremationService.address.province'),
            getLabel: ({ description }) => description,
            getValue: ({ code }) => code,
            name: 'address.p',
            onBlur: handleBlur,
            onChange: (val: string) => setFieldValue('cremationService.address.province', val),
            placeholder: t('common.province'),
            value: values.cremationService?.address?.province || '',
          }}
        />
        <Select<CrematoryType>
          key="crematorium"
          disabled={
            isSubmitting
            || disabled
            || !values.cremationService?.address?.city
          }
          error={getIn(errors, 'cremationService.crematorium')}
          getLabel={({ description }) => description}
          getValue={({ code }) => code}
          name="crematorium"
          options={crematories}
          placeholder={t('service.cremation.crematorium')}
          value={
            values.cremationService?.crematorium
              ? normalizedCrematories[values.cremationService?.crematorium]
              : undefined
          }
          searchable
          onBlur={handleBlur}
          onChange={(val) => {
            setFieldValue('cremationService.crematorium', val?.code);
            setFieldValue('cremationService.address.oven', undefined);
            setFieldValue('cremationService.work', val?.work);
          }}
        />
        <Datepicker
          key="cremationDate"
          disabled={
            disabled || isSubmitting || !values.cremationService
          }
          error={getIn(errors, 'cremationService.cremationDate')}
          name="cremationDate"
          placeholder={t('service.cremation.cremationDate')}
          selected={values.cremationService?.cremationDate}
          onBlur={handleBlur}
          onChange={(val) => setFieldValue('cremationService.cremationDate', val)}
        />
        <Datepicker
          key="cremationTime"
          disabled={disabled || isSubmitting || !values.cremationService}
          error={getIn(errors, 'cremationService.cremationTime')}
          name="cremationTime"
          placeholder={t('service.cremation.cremationTime')}
          selected={values.cremationService?.cremationTime}
          showTimeSelectOnly
          onBlur={handleBlur}
          onChange={(val) => setFieldValue('cremationService.cremationTime', val)}
        />
        <Textarea
          key="commentCremation"
          disabled={isSubmitting || disabled || !values.cremationService}
          error={getIn(errors, 'cremationService.commentCremation')}
          id="commentCremation"
          name="commentCremation"
          placeholder={t('common.observations')}
          value={values.cremationService?.commentCremation || ''}
          onBlur={handleBlur}
          onChange={(val) => setFieldValue('cremationService.commentCremation', val)}
        />
        <div />

        <Input
          checked={!!values.vigilService}
          disabled={disabled || isSubmitting || values.hasPickUp}
          error={getIn(errors, 'vigilService')}
          id="vigilService"
          name="vigilService"
          placeholder={t('service.VELATORIO')}
          type="checkbox"
          onBlur={handleBlur}
          onChange={(val) => {
            setFieldValue('vigilService', val ? {} : null);
          }}
        />
        <div />
        <SelectCountryProvinceCity
          key="address"
          cityProps={{
            error: getIn(errors, 'vigilService.address.city'),
            getLabel: ({ description }) => description,
            getValue: ({ code }) => code,
            name: 'address.c',
            onBlur: handleBlur,
            onChange: (val: string) => {
              setFieldValue('vigilService.address.city', val);
              setFieldValue('vigilService.location', undefined);
            },
            placeholder: t('common.city'),
            value: values.vigilService?.address?.city || '',
          }}
          countryProps={{
            error: getIn(errors, 'vigilService.address.country'),
            getLabel: ({ description }) => description,
            getValue: ({ code }) => code,
            name: 'address.co',
            onBlur: handleBlur,
            onChange: (val: string) => setFieldValue('vigilService.address.country', val),
            placeholder: t('common.country'),
            value: values.vigilService?.address?.country || '',
          }}
          disabled={disabled || isSubmitting || !values.vigilService}
          initialize={false}
          provinceProps={{
            error: getIn(errors, 'vigilService.address.province'),
            getLabel: ({ description }) => description,
            getValue: ({ code }) => code,
            name: 'address.p',
            onBlur: handleBlur,
            onChange: (val: string) => setFieldValue('vigilService.address.province', val),
            placeholder: t('common.province'),
            value: values.vigilService?.address?.province || '',
          }}
        />
        <Select<VigilLocation>
          key="location"
          disabled={
            isSubmitting
            || disabled
            || !values.vigilService?.address?.city
          }
          error={getIn(errors, 'vigilService.location')}
          getLabel={({ description }) => description}
          getValue={({ code }) => code}
          name="location"
          options={vigilLocations}
          placeholder={t('common.location')}
          value={
            values.vigilService?.location
              ? normalizedVigilLocations[values.vigilService?.location]
              : undefined
          }
          searchable
          onBlur={handleBlur}
          onChange={(val) => {
            setFieldValue('vigilService.location', val?.code);
          }}
        />
        <Datepicker
          key="entryDate"
          disabled={disabled || isSubmitting || !values.vigilService}
          error={getIn(errors, 'vigilService.entryDate')}
          name="entryDate"
          placeholder={t('common.dateIn')}
          selected={values.vigilService?.entryDate}
          onBlur={handleBlur}
          onChange={(val) => setFieldValue('vigilService.entryDate', val)}
        />
        <Datepicker
          key="entryTime"
          disabled={disabled || isSubmitting || !values.vigilService}
          error={getIn(errors, 'vigilService.entryTime')}
          name="entryTime"
          placeholder={t('common.hourIn')}
          selected={values.vigilService?.entryTime}
          showTimeSelectOnly
          onBlur={handleBlur}
          onChange={(val) => setFieldValue('vigilService.entryTime', val)}
        />
        <Textarea
          key="comment"
          disabled={isSubmitting || disabled || !values.vigilService}
          error={getIn(errors, 'vigilService.comment')}
          id="comment"
          name="comment"
          placeholder={t('common.observations')}
          value={values.vigilService?.comment || ''}
          onBlur={handleBlur}
          onChange={(val) => setFieldValue('vigilService.comment', val)}
        />
        <div />

        <Input
          checked={!!values.ceremonyService}
          disabled={disabled || isSubmitting || values.hasPickUp}
          error={getIn(errors, 'ceremonyService')}
          id="ceremonyService"
          name="ceremonyService"
          placeholder={t('service.CEREMONIA')}
          type="checkbox"
          onBlur={handleBlur}
          onChange={(val) => {
            setFieldValue('ceremonyService', val ? {} : null);
          }}
        />
        <div />
        <SelectCountryProvinceCity
          key="ceremonyAddress"
          cityProps={{
            error: getIn(errors, 'ceremonyService.ceremonyAddress.city'),
            getLabel: ({ description }) => description,
            getValue: ({ code }) => code,
            name: 'ceremonyAddress.c',
            onBlur: handleBlur,
            onChange: (val: string) => {
              setFieldValue('ceremonyService.ceremonyAddress.city', val);
              setFieldValue('ceremonyService.location', undefined);
            },
            placeholder: t('common.city'),
            value: values.ceremonyService?.ceremonyAddress?.city || '',
          }}
          countryProps={{
            error: getIn(errors, 'ceremonyService.ceremonyAddress.country'),
            getLabel: ({ description }) => description,
            getValue: ({ code }) => code,
            name: 'ceremonyAddress.co',
            onBlur: handleBlur,
            onChange: (val: string) => setFieldValue('ceremonyService.ceremonyAddress.country', val),
            placeholder: t('common.country'),
            value: values.ceremonyService?.ceremonyAddress?.country || '',
          }}
          disabled={disabled || isSubmitting || !values.ceremonyService}
          initialize={false}
          provinceProps={{
            error: getIn(errors, 'ceremonyService.ceremonyAddress.province'),
            getLabel: ({ description }) => description,
            getValue: ({ code }) => code,
            name: 'ceremonyAddress.p',
            onBlur: handleBlur,
            onChange: (val: string) => setFieldValue('ceremonyService.ceremonyAddress.province', val),
            placeholder: t('common.province'),
            value: values.ceremonyService?.ceremonyAddress?.province || '',
          }}
        />
        <Select<IMasterDataDto>
          key="ceremony-location"
          disabled={
            isSubmitting
            || disabled
            || !values.ceremonyService?.ceremonyAddress?.province
          }
          error={getIn(errors, 'ceremonyService.location')}
          getLabel={({ description }) => description}
          getValue={({ code }) => code}
          name="location"
          options={ceremonyLocations}
          placeholder={t('common.location')}
          value={
            values.ceremonyService?.location
              ? normalizedCeremonyLocations[values.ceremonyService?.location]
              : undefined
          }
          searchable
          onBlur={handleBlur}
          onChange={(val) => setFieldValue('ceremonyService.location', val?.code)}
        />
        <Select<IMasterDataDto>
          key="type"
          disabled={isSubmitting || disabled || !values.ceremonyService}
          error={getIn(errors, 'ceremonyService.type')}
          getLabel={({ description }) => description}
          getValue={({ code }) => code}
          name="type"
          options={ceremonies}
          placeholder={t('service.ceremony.type')}
          value={
            values.ceremonyService?.type
              ? normalized.ceremonies[values.ceremonyService?.type]
              : undefined
          }
          searchable
          onBlur={handleBlur}
          onChange={(val) => setFieldValue('ceremonyService.type', val?.code)}
        />
        <Datepicker
          key="ceremonyDate"
          disabled={disabled || isSubmitting || !values.ceremonyService}
          error={getIn(errors, 'ceremonyService.ceremonyDate')}
          name="ceremonyDate"
          placeholder={t('common.date')}
          selected={values.ceremonyService?.ceremonyDate}
          onBlur={handleBlur}
          onChange={(val) => {
            setFieldValue('ceremonyService.ceremonyDate', val);
          }}
        />
        <Datepicker
          key="ceremonyTime"
          disabled={disabled || isSubmitting || !values.ceremonyService}
          error={getIn(errors, 'ceremonyService.ceremonyTime')}
          name="ceremonyTime"
          placeholder={t('common.hour')}
          selected={values.ceremonyService?.ceremonyTime}
          showTimeSelectOnly
          onBlur={handleBlur}
          onChange={(val) => {
            setFieldValue('ceremonyService.ceremonyTime', val);
          }}
        />
        <Textarea
          key="ceremonyComment"
          disabled={isSubmitting || disabled || !values.ceremonyService}
          error={getIn(errors, 'ceremonyService.ceremonyComment')}
          name="ceremonyComment"
          placeholder={t('common.observations')}
          value={values.ceremonyService?.ceremonyComment || ''}
          onBlur={handleBlur}
          onChange={(val) => setFieldValue('ceremonyService.ceremonyComment', val)}
        />
      </fieldset>
    </React.Fragment>
  );
};
export default ManifestIntentionFields;
